/**
 * What a virtual machine can be made of (ADR-0150): an image AFAR builds and tests, one of AFAR's
 * sizes, and a disk. Sizes are named by what they give, not by any provider's machine type; each
 * compute adapter maps them to its own.
 */
export interface VirtualMachineImage {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  /** Whether the image has a desktop, and so a Cloud Display. */
  readonly desktop: boolean;
}
export interface VirtualMachineSize {
  readonly id: string;
  readonly label: string;
  readonly vcpu: number;
  readonly memoryGb: number;
}

export const VIRTUAL_MACHINE_IMAGES: readonly VirtualMachineImage[] = [
  {
    id: "ubuntu-24.04-desktop",
    label: "Ubuntu 24.04 with desktop",
    description: "XFCE desktop, Chromium, Docker, and sudo",
    desktop: true,
  },
  {
    id: "ubuntu-24.04-server",
    label: "Ubuntu 24.04",
    description: "No desktop; Chromium, Docker, and sudo",
    desktop: false,
  },
];
export const DEFAULT_VIRTUAL_MACHINE_IMAGE = "ubuntu-24.04-desktop";

export const VIRTUAL_MACHINE_SIZES: readonly VirtualMachineSize[] = [
  { id: "small", label: "Small", vcpu: 2, memoryGb: 4 },
  { id: "medium", label: "Medium", vcpu: 2, memoryGb: 8 },
  { id: "large", label: "Large", vcpu: 4, memoryGb: 16 },
  { id: "xlarge", label: "Extra large", vcpu: 8, memoryGb: 32 },
];

/** A disk holds the image, the Host Store, and the repositories; it can grow but never shrink. */
export const VIRTUAL_MACHINE_DISK_GB = { min: 30, max: 500, default: 50 } as const;

export const virtualMachineImage = (id: string) =>
  VIRTUAL_MACHINE_IMAGES.find((image) => image.id === id);
export const virtualMachineSize = (id: string) =>
  VIRTUAL_MACHINE_SIZES.find((size) => size.id === id);

export const validDiskGb = (diskGb: number) =>
  Number.isSafeInteger(diskGb) &&
  diskGb >= VIRTUAL_MACHINE_DISK_GB.min &&
  diskGb <= VIRTUAL_MACHINE_DISK_GB.max;
